import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Button, Snackbar, Alert, TextField, MenuItem, FormControl, Select, Grid, Box, Typography, CircularProgress, Card, CardContent, CardHeader, Divider } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Delete as DeleteIcon } from '@mui/icons-material';

import axios from 'axios';

export const STATUS = {
  PENDING: "Pending",
  IN_PROGRESS: "In Progress",
  MORE_INFO_NEEDED: "More Info Needed",
  SEND_TO_TESTING: "Send to Testing",
  FAILED_TESTING: "Failed Testing",
  DONE: "Done"
};

const AdminBugReportDashboard = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    const results = reports.filter(report =>
      Object.values(report).some(value =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredReports(results);
  }, [searchTerm, reports]);

  const fetchReports = async () => {
    try {
      const response = await axios.get('https://ve502n7sng.execute-api.us-west-2.amazonaws.com/beta/reports');
      setReports(response.data);
      setFilteredReports(response.data);
      //console.log("Number of Reports", response.data.length);
      //console.log(JSON.stringify(response.data));

    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (bugId, status) => {
    try {
      await axios.patch(`https://ve502n7sng.execute-api.us-west-2.amazonaws.com/beta/report-bug/${bugId}`, { status });
      setSnackbarSeverity('success');
      setSnackbarMessage('Report status updated successfully!');
      setSnackbarOpen(true);
      fetchReports(); // Refresh the reports list
    } catch (error) {
      console.error('Error updating report status:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to update report status. Please try again later.');
      setSnackbarOpen(true);
    }
  };

  const handleMarkAsDone = async (bugId) => {
    await handleUpdateStatus(bugId, STATUS.DONE);
  };

  const handleDeleteReport = async (bugId) => {
    try {
      await axios.delete(`https://ve502n7sng.execute-api.us-west-2.amazonaws.com/beta/report-bug/${bugId}`);
      setSnackbarSeverity('success');
      setSnackbarMessage('Report deleted successfully!');
      setSnackbarOpen(true);
      fetchReports(); // Refresh the reports list
    } catch (error) {
      console.error('Error deleting report:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to delete report. Please try again later.');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const activeReports = filteredReports.filter(report => report.status !== STATUS.DONE);
  const completedReports = filteredReports.filter(report => report.status === STATUS.DONE);
  const sortedActiveReports = [...activeReports].sort((a, b) => new Date(b.reportDate) - new Date(a.reportDate));
  const sortedCompletedReports = [...completedReports].sort((a, b) => new Date(b.reportDate) - new Date(a.reportDate));

  return (
    <Box sx={{ padding: 3 }}>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Typography variant="h4" gutterBottom>
        Bug Report Dashboard
      </Typography>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            variant="outlined"
            label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: <SearchIcon />,
            }}
          />
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <CircularProgress />
        </Box>
      ) : (
        <Box mt={4}>
          <Card variant="outlined">
            <CardHeader title="Active Bug Reports" titleTypographyProps={{ variant: 'h5' }} />
            <CardContent>
              <div style={{marginBottom: 15}}>
                <p><strong>Number of Current Active Bugs:</strong> {activeReports.length}</p>
              </div>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#10766e' }}>
                      <TableCell sx={{ color: 'white' }}>Report ID</TableCell>
                      <TableCell sx={{ color: 'white' }}>Type</TableCell>
                      <TableCell sx={{ color: 'white' }}>Page Name</TableCell>
                      <TableCell sx={{ color: 'white' }}>Description</TableCell>
                      <TableCell sx={{ color: 'white' }}>Bug Location</TableCell>
                      <TableCell sx={{ color: 'white' }}>Additional Details</TableCell>
                      <TableCell sx={{ color: 'white' }}>Reported By</TableCell>
                      <TableCell sx={{ color: 'white' }}>Date Reported</TableCell>
                      <TableCell sx={{ color: 'white' }}>Status</TableCell>
                      <TableCell sx={{ color: 'white' }}>Screenshots</TableCell>
                      <TableCell sx={{ color: 'white' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {sortedActiveReports.map((report) => (
                      <TableRow key={report.bugId}>
                        <TableCell>{report.bugId}</TableCell>
                        <TableCell>{report.bugType}</TableCell>
                        <TableCell>{report.pageName}</TableCell>
                        <TableCell>{report.description}</TableCell>
                        <TableCell>
                          <ul style={{ paddingLeft: 20, listStyleType: 'disc' }}>
                              <li>Geolocation: {report.searchedLatitude}, {report.searchedLongitude}</li>
                              <li>
                                {report.searchedLatitude && report.searchedLongitude ? (
                                  <a href={`https://www.google.com/maps?q=${report.searchedLatitude},${report.searchedLongitude}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>
                                    View on Google Map
                                  </a>
                                ) : (
                                  'N/A'
                                )}
                              </li>
                            </ul>
                        </TableCell>
                        <TableCell>
                          <ul style={{ paddingLeft: 20, listStyleType: 'disc' }}>
                            <li>Desired Parking Time: {report.desiredParkingTime}</li>
                            <li>Place ID: {report.placeId}</li>
                            <li>Arrival Date/Time: {report.arrivalDateTime}</li>
                            <li>Searched Address: {report.searchedAddress}</li>
                          </ul>
                        </TableCell>
                        <TableCell>{report.reportBy}</TableCell>
                        <TableCell>{report.reportDate}</TableCell>
                        <TableCell>
                          <FormControl fullWidth>
                            <Select
                              value={report.status || STATUS.PENDING}
                              onChange={(e) => handleUpdateStatus(report.bugId, e.target.value)}
                            >
                              {Object.values(STATUS).filter(status => status !== STATUS.DONE).map((status) => (
                                <MenuItem key={status} value={status}>
                                  {status}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <ul style={{ paddingLeft: 20, listStyleType: 'disc' }}>
                            {report.screenshotUrls && report.screenshotUrls.map((url, index) => (
                              <li key={index}>
                                <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>
                                  Screenshot {index + 1}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </TableCell>

                        <TableCell>
                          <Button variant="contained" color="primary" onClick={() => handleMarkAsDone(report.bugId)}>
                            Mark as Done
                          </Button>
                          <IconButton color="error" onClick={() => handleDeleteReport(report.bugId)} sx={{ ml: 1 }}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ marginTop: 4 }}>
            <CardHeader title="Bugs Completed" titleTypographyProps={{ variant: 'h5' }} />
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#10766e' }}>
                      <TableCell sx={{ color: 'white' }}>Report ID</TableCell>
                      <TableCell sx={{ color: 'white' }}>Type</TableCell>
                      <TableCell sx={{ color: 'white' }}>Page Name</TableCell>
                      <TableCell sx={{ color: 'white' }}>Place ID</TableCell>
                      <TableCell sx={{ color: 'white' }}>Description</TableCell>
                      <TableCell sx={{ color: 'white' }}>Reported By</TableCell>
                      <TableCell sx={{ color: 'white' }}>Date Reported</TableCell>
                      <TableCell sx={{ color: 'white' }}>Screenshots</TableCell>
                      <TableCell sx={{ color: 'white' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {sortedCompletedReports.map((report) => (
                      <TableRow key={report.bugId}>
                        <TableCell>{report.bugId}</TableCell>
                        <TableCell>{report.bugType}</TableCell>
                        <TableCell>{report.pageName}</TableCell>
                        <TableCell>{report.placeId}</TableCell>
                        <TableCell>{report.description}</TableCell>
                        <TableCell>{report.reportBy}</TableCell>
                        <TableCell>{report.reportDate}</TableCell>
                        <TableCell>
                          <ul style={{ paddingLeft: 20, listStyleType: 'disc' }}>
                            {report.screenshotUrls && report.screenshotUrls.map((url, index) => (
                              <li key={index}>
                                <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>
                                  Screenshot {index + 1}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </TableCell>

                        <TableCell>
                          <Button variant="contained" color="secondary" onClick={() => handleUpdateStatus(report.bugId, STATUS.FAILED_TESTING)}>
                            Move to Active Bug
                          </Button>
                        </TableCell>

                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default AdminBugReportDashboard;
